/**
 * Slide navigation overlay for agent-backed presentations in flow-control preview.
 * When the VM video track is shown via livekitTrackMediaAgent, the imageSequence shield is hidden —
 * this overlay keeps prev/next and go-to-slide controls on top of the LiveKit layer.
 */

import { useEffect } from 'react';
import { Box } from '@mui/material';
import type { OutputLayer } from '@/components/preview/output-layer-preview';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import { PreviewPresentationSlidesLayer } from '@/components/preview/preview-presentation-slides-layer';
import { resolvePreviewSlideOverlayZIndex } from '@/helpers/preview/preview-chrome-z-index.helper';
import { normalizeMediaItemId, resolveLayerSequenceItemId } from '@/helpers/resolve-live-sequence-item-ids.helper';
import { useLiveSlideChromeSuppressStore } from '@/state/stores/use-live-slide-chrome-suppress.store';

const SEQUENCED_COMPONENT_IDS = new Set(['imageSequence', 'presentationSlides']);

/**
 * Prefer the on-air cue / played media (what Current playout already shows) over
 * `activeAgentDeckMediaId`, which lags until the media-agent LiveKit track catches up.
 */
function resolveActiveAgentDeckMediaId(liveContextState?: LiveContextStateDto): string | undefined {
  const fromCue = normalizeMediaItemId(liveContextState?.cueState?.currentStep?.itemId);
  if (fromCue?.startsWith('media:')) {
    return fromCue;
  }
  const playedMedia = liveContextState?.playState?.playedItemIds?.find((entry) => entry.itemId?.startsWith('media:'));
  const fromPlayed = normalizeMediaItemId(playedMedia?.itemId);
  if (fromPlayed?.startsWith('media:')) {
    return fromPlayed;
  }
  return normalizeMediaItemId(liveContextState?.playState?.activeAgentDeckMediaId);
}

function isAgentInfraLayerOnAir(layer: OutputLayer): boolean {
  return (
    layer.componentId === 'livekitTrackMediaAgent' &&
    layer.visible !== false &&
    Boolean(layer.itemId?.startsWith('mediaAgent:'))
  );
}

export function AgentDeckLiveSlideOverlay({
  layers,
  liveContextState,
  entityRef,
}: {
  layers: OutputLayer[];
  liveContextState?: LiveContextStateDto;
  /** Required to clear local advance suppress when Current settles. */
  entityRef?: string;
}) {
  const presentationTransitionInFlight = liveContextState?.playState?.presentationTransitionInFlight === true;
  const currentItemId = liveContextState?.cueState?.currentStep?.itemId;

  useEffect(() => {
    if (!entityRef) {
      return;
    }
    // Current preview owns clear-while-inFlight for non-agent takes. This overlay only
    // hides agent slide chrome; do not re-pin suppress while the outgoing PPT shield is up.
    if (presentationTransitionInFlight) {
      return;
    }
    useLiveSlideChromeSuppressStore.getState().syncClearAfterAdvance(entityRef, {
      currentItemId,
      presentationTransitionInFlight: false,
    });
  }, [entityRef, currentItemId, presentationTransitionInFlight]);

  // Hide while BE presentation shield is up. Once the agent track is on air (same moment the
  // deck is visible), show y/x immediately — do not wait on local chrome suppress grace.
  if (presentationTransitionInFlight) {
    return null;
  }
  const activeDeck = resolveActiveAgentDeckMediaId(liveContextState);
  if (!activeDeck) {
    return null;
  }
  if (!layers.some(isAgentInfraLayerOnAir)) {
    return null;
  }

  const sequencedLayer = layers.find((layer) => {
    if (!SEQUENCED_COMPONENT_IDS.has(layer.componentId)) {
      return false;
    }
    if (resolveLayerSequenceItemId(layer) !== activeDeck) {
      return false;
    }
    return Boolean(layer.data?.slideCount || layer.data?.frameCount || layer.data?.imageSequenceUrls);
  });
  if (!sequencedLayer) {
    return null;
  }

  const sequencedItemId = resolveLayerSequenceItemId(sequencedLayer) ?? sequencedLayer.itemId;
  const overlayZIndex = resolvePreviewSlideOverlayZIndex(layers);

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: overlayZIndex,
        pointerEvents: 'none',
      }}
    >
      <PreviewPresentationSlidesLayer
        data={sequencedLayer.data}
        itemId={sequencedItemId}
        componentId={sequencedLayer.componentId as 'imageSequence' | 'presentationSlides'}
        slideNavigationScope="live"
        sequencePlaybackControls={false}
        controlsOnly
        playVideoOverlays={false}
      />
    </Box>
  );
}
